/**
 * Service für Spieler-Welt-Operationen.
 * Lookup, Beitritt, Planeten-Abfrage, letzte Welt.
 */
import type { SupabaseClient } from '../network/SupabaseClient';
import type {
  PlayerWorld, WorldSummary, JoinResult, PlanetSummary, PlanetDetail, PlanetInfo,
} from './PlayerWorldTypes';

/** players.id aus auth_id nachschlagen. */
export async function lookupPlayerId(supabase: SupabaseClient, authId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('players')
    .select('id')
    .eq('auth_id', authId)
    .single();
  if (error || !data) return null;
  return data.id as string;
}

/** Alle Welten, denen der Spieler beigetreten ist. */
export async function getPlayerWorlds(supabase: SupabaseClient, playerId: string): Promise<PlayerWorld[]> {
  const { data, error } = await supabase
    .from('world_players')
    .select('world_id, joined_at, worlds(name)')
    .eq('player_id', playerId);
  if (error || !data) return [];
  return data.map((row: Record<string, unknown>) => ({
    worldId: row.world_id as string,
    worldName: (row.worlds as Record<string, string>)?.name ?? '?',
    joinedAt: row.joined_at as string,
  }));
}

/** Alle verfügbaren Welten mit Spielerzahl. */
export async function getAvailableWorlds(supabase: SupabaseClient): Promise<WorldSummary[]> {
  const { data: worlds, error } = await supabase
    .from('worlds')
    .select('id, name, state, seed')
    .in('state', ['generated', 'active'])
    .order('created_at', { ascending: false });
  if (error || !worlds) return [];

  const { data: counts } = await supabase
    .from('world_players')
    .select('world_id');
  const countMap = new Map<string, number>();
  if (counts) {
    for (const row of counts) {
      const wid = row.world_id as string;
      countMap.set(wid, (countMap.get(wid) ?? 0) + 1);
    }
  }

  return worlds.map((w: Record<string, unknown>) => ({
    id: w.id as string,
    name: w.name as string,
    state: w.state as string,
    playerCount: countMap.get(w.id as string) ?? 0,
    seed: w.seed as number,
  }));
}

/** Welt beitreten via Edge Function. */
export async function joinWorld(supabase: SupabaseClient, worldId: string): Promise<JoinResult | null> {
  const res = await supabase.functions.invoke('join-world', {
    body: { world_id: worldId },
  });
  if (res.error) return null;
  const body = res.data as Record<string, unknown>;
  if (body['error']) return null;
  const planet = body['planet'] as Record<string, unknown> | null;
  return {
    playerId: body['player_id'] as string,
    planet: planet ? mapPlanet(planet) : null,
    worldName: body['world_name'] as string,
  };
}

/** Beanspruchte Planeten des Spielers in einer Welt. */
export async function getPlayerPlanets(
  supabase: SupabaseClient, playerId: string, worldId: string,
): Promise<PlanetSummary[]> {
  const { data, error } = await supabase
    .from('planets')
    .select('id, axial_q, axial_r, cluster_col, cluster_row, planet_type')
    .eq('world_id', worldId)
    .eq('claimed_by', playerId);
  if (error || !data) return [];
  return data.map((p: Record<string, unknown>) => mapPlanet(p));
}

/** Vollständige Planeten-Daten mit Ressourcen. */
export async function getPlayerPlanetDetails(
  supabase: SupabaseClient, playerId: string, worldId: string,
): Promise<PlanetDetail[]> {
  const { data, error } = await supabase
    .from('planets')
    .select('id, axial_q, axial_r, cluster_col, cluster_row, planet_type, moon_count, has_rings, orbital_ring, feronium, feronium_cap, chrysalium, chrysalium_cap, neurogel, neurogel_cap, star_system_id, star_systems(luminosity)')
    .eq('world_id', worldId)
    .eq('claimed_by', playerId);
  if (error || !data) return [];
  return data.map((p: Record<string, unknown>) => mapPlanetDetail(p));
}

/** Alle Planeten der Welt mit Besitz-Status. */
export async function getWorldPlanets(
  supabase: SupabaseClient, worldId: string, playerId?: string,
): Promise<PlanetInfo[]> {
  const { data, error } = await supabase
    .from('planets')
    .select('id, axial_q, axial_r, cluster_col, cluster_row, planet_type, moon_count, has_rings, orbital_ring, feronium, feronium_cap, chrysalium, chrysalium_cap, neurogel, neurogel_cap, claimed_by, star_system_id, star_systems(luminosity)')
    .eq('world_id', worldId);
  if (error || !data) return [];
  return data.map((p: Record<string, unknown>) => {
    const claimedBy = p['claimed_by'] as string | null;
    const ownership = !claimedBy ? 'unclaimed'
      : (playerId && claimedBy === playerId) ? 'own' : 'other';
    return { ...mapPlanetDetail(p), ownership };
  });
}

/** last_world_id aus players lesen. */
export async function getLastWorldId(supabase: SupabaseClient, playerId: string): Promise<string | null> {
  const { data } = await supabase
    .from('players')
    .select('last_world_id')
    .eq('id', playerId)
    .single();
  return (data?.last_world_id as string) ?? null;
}

/** last_world_id setzen via join-world (handelt auch already-joined). */
export async function updateLastWorld(supabase: SupabaseClient, worldId: string): Promise<void> {
  await supabase.functions.invoke('join-world', { body: { world_id: worldId } });
}

function mapPlanet(p: Record<string, unknown>): PlanetSummary {
  return {
    id: p['id'] as string,
    axialQ: p['axial_q'] as number,
    axialR: p['axial_r'] as number,
    clusterCol: p['cluster_col'] as number,
    clusterRow: p['cluster_row'] as number,
    planetType: p['planet_type'] as string,
  };
}

function mapPlanetDetail(p: Record<string, unknown>): PlanetDetail {
  const starSys = p['star_systems'] as Record<string, unknown> | null;
  return {
    ...mapPlanet(p),
    starSystemId: (p['star_system_id'] as string) ?? '',
    sunLuminosity: (starSys?.['luminosity'] as number) ?? 1,
    moonCount: p['moon_count'] as number,
    hasRings: p['has_rings'] as boolean,
    orbitalRing: p['orbital_ring'] as number,
    feronium: Number(p['feronium']),
    feroniumCap: Number(p['feronium_cap']),
    chrysalium: Number(p['chrysalium']),
    chrysaliumCap: Number(p['chrysalium_cap']),
    neurogel: Number(p['neurogel']),
    neurogelCap: Number(p['neurogel_cap']),
  };
}
